import { ActionsCreator, clearHandler, chainReducers } from '../lib'
import { screenApi } from 'apis'
import { loadStatsForIntercom, intercomLogout } from 'intercom'
import { COOKIE_KEYS, loadCookie, saveCookie, deleteCookie } from 'helpers/cookies'
import { urlFactory } from 'helpers/url'
import { CurrentUserSchema } from '../schemas'
import {
  fetchReferralWallet,
  fetchWishlists,
  fetchCurrentUsers,
  fetchNotifications,
  toggleLoginModal,
  toggleRegisterModal,
  toggleEmailValidationForm,
  setAppCurrency,
  gtmUserLoggedIn,
  gtmUserLoggedOut,
  gtmRegistered,
  setAppLocale,
  appInitialised,
} from 'redux/actions'
import {
  getCurrentUser,
  getCurrentUserCurrency,
  getCurrentUserLanguage,
  getLocation,
  getIsLoginModalVisible,
} from 'redux/selectors'
import { apiErrorCodes } from '@vizeat/helpers'

export const authActions = {
  LOGOUT: '@@account/LOGOUT',
}

export const accountActions = new ActionsCreator({ Api: screenApi, actionTypesPrefix: 'account' })

// RENEW PASSWORD
function createRenewPassword(email) {
  return accountActions.create({
    url: '/password_renewal',
    payload: { email },
    schema: { user: CurrentUserSchema },
  })
}

function updateRenewPassword(token, password) {
  return async (dispatch) => {
    const result = await dispatch(
      accountActions.update({
        url: '/password_renewal',
        payload: { password, validation_token: token },
        schema: { user: CurrentUserSchema },
      }),
    )
    try {
      return dispatch(setAuthenticationData({ type: 'email', result }))
    } catch (error) {
      await dispatch(removeAuthenticationData())
      throw error
    }
  }
}

// VALIDATE ACCOUNT
function validateAccount(token) {
  return async (dispatch) => {
    const result = await dispatch(
      accountActions.create({
        url: '/accounts/validate',
        payload: { validation_token: token },
        schema: { user: CurrentUserSchema },
      }),
    )
    try {
      return dispatch(setAuthenticationData({ type: 'email', result }))
    } catch (error) {
      await dispatch(removeAuthenticationData())
      throw error
    }
  }
}

function resendAccountValidationEmail(email) {
  return (dispatch) =>
    dispatch(
      accountActions.create({
        url: `/accounts/validate/${email}`,
        schema: {},
      }),
    )
}

// AUTH
function setCookies(token, language, rememberMe) {
  screenApi.setHeader('Authorization', `JWT ${token}`)
  screenApi.setHeader('X-Language', language.locale)

  saveCookie({ name: COOKIE_KEYS.TOKEN, value: token, duration: 3600 * 24 * 60 })
  saveCookie({
    name: COOKIE_KEYS.NEWSLETTER_SIGNUP,
    value: true,
    duration: 365 * 24 * 3600,
  })
  saveCookie({
    name: COOKIE_KEYS.REMEMBER_ME_AUTHENTICATION,
    value: rememberMe,
    duration: 3600 * 24 * 90,
  })
}

function setAuthenticationData({
  type,
  result: {
    payload: { data, error },
  },
  cookies,
}) {
  return async (dispatch, getState) => {
    if (error?.data?.code === apiErrorCodes.EMAIL_NOT_VALIDATED) {
      await dispatch(toggleEmailValidationForm({ show: true }))
      throw new Error(error?.data?.message)
    }

    const token = data?.result?.token || cookies?.cookieToken
    if (!data?.result?.user || !token) throw new Error(`Missing data after ${type} login`)

    const state = getState()
    const user = getCurrentUser(state)
    const currency = getCurrentUserCurrency(state)
    const language = getCurrentUserLanguage(state)
    const isSocialAuth = ['facebook', 'google', 'weibo'].includes(type)
    const rememberMe = isSocialAuth || cookies?.cookieRememberMe
    setCookies(token, language, rememberMe)

    await dispatch(setAppCurrency(currency.iso_3))
    await dispatch(setAppLocale(language.locale))

    await dispatch(fetchNotifications(0))
    await dispatch(fetchReferralWallet())
    await dispatch(fetchWishlists())
    await dispatch(gtmUserLoggedIn({ user, country: user.country, type }))
    await dispatch(toggleLoginModal({ show: false }))
    await dispatch(toggleRegisterModal(false))
    const { data: stats } = await screenApi.get('/users/stats')
    return loadStatsForIntercom(user, stats)
  }
}

function removeAuthenticationData(redirect = true) {
  return async (dispatch, getState) => {
    const state = getState()
    if (redirect && (getLocation(state).pathname || '').startsWith('/dashboard')) {
      window.location = urlFactory.web.home()
    }
    intercomLogout()
    return dispatch(gtmUserLoggedOut())
  }
}
// REGISTER
function registerWithEmail(payload) {
  return async (dispatch) => {
    const {
      payload: {
        payload: resultPayload,
        data: {
          result: { user, created },
        },
      },
    } = await dispatch(accountActions.create({ url: '/register', payload, schema: {} }))

    await dispatch(toggleEmailValidationForm({ show: true }))
    if (!user || !created) throw new Error(`Something went wrong during registration`)
    return dispatch(gtmRegistered({ ...resultPayload, id: user.id }, resultPayload.type, created))
  }
}

// LOGIN
function loginWithEmail(payload) {
  return async (dispatch) => {
    const result = await dispatch(
      accountActions.create({
        url: '/login',
        payload,
        schema: { user: CurrentUserSchema },
      }),
    )

    const cookies = { cookieRememberMe: payload.rememberMe }
    return dispatch(setAuthenticationData({ type: 'email', result, cookies }))
  }
}

function authByCookie() {
  return async (dispatch) => {
    screenApi.removeHeader('Authorization')
    const cookieToken = loadCookie(COOKIE_KEYS.TOKEN)
    const cookieRememberMe = loadCookie(COOKIE_KEYS.REMEMBER_ME_AUTHENTICATION) === 'true'
    const cookieIsAdmin = loadCookie(COOKIE_KEYS.VIZEAT_ADMIN) === 'true'
    if (!cookieToken || (!cookieIsAdmin && !cookieRememberMe)) {
      await dispatch(appInitialised())
      return dispatch(removeAuthenticationData(false))
    }
    try {
      const result = await dispatch(
        fetchCurrentUsers({
          headers: Object.assign(screenApi.config.headers, { authorization: `JWT ${cookieToken}` }),
        }),
      )
      await dispatch(appInitialised())
      return dispatch(
        setAuthenticationData({
          type: 'cookie',
          result,
          cookies: { cookieToken, cookieRememberMe },
        }),
      )
    } catch (error) {
      await dispatch(removeAuthenticationData())
      throw error
    }
  }
}

// SOCIAL
function authBySocial(socialName, payload) {
  return async (dispatch, getState) => {
    const result = await dispatch(
      accountActions.create({
        url: `/login/${socialName}`,
        payload: {
          ...payload,
          access_token: payload.authResponse.accessToken || payload.authResponse.access_token,
        },
        schema: { user: CurrentUserSchema },
      }),
    )
    const { error, data } = result.payload

    const isUserRegistration = error?.data?.code === apiErrorCodes.EMAIL_NOT_VALIDATED
    if (error && !isUserRegistration) return

    // Register via social
    if (isUserRegistration) {
      const state = getState()
      if (getIsLoginModalVisible(state)) {
        await dispatch(toggleLoginModal({ show: false }))
        await dispatch(toggleRegisterModal(true, true))
      }
      return dispatch(toggleEmailValidationForm({ show: true }))
    }

    // Login via social
    if (!data.result.user || !data.result.token) throw new Error(`Missing data after registering with ${socialName}`)
    await dispatch(toggleLoginModal({ show: false }))
    return dispatch(setAuthenticationData({ type: socialName, result }))
  }
}

const loginWithWeibo = (code) => (dispatch, getState) => {
  return dispatch(
    accountActions.create({
      url: '/login/weibo_new',
      payload: { code },
      schema: { user: CurrentUserSchema },
    }),
  ).then((result) => {
    if (result.payload.error) return
    if (result.payload.data.result.created) {
      // is new, register
      dispatch(toggleRegisterModal(true, true))
      if (!result.payload.data.result.user || !result.payload.data.result.token)
        throw new Error(`Missing data after registering with weibo`)
      const language = getCurrentUserLanguage(getState())
      return setCookies(result.payload.data.result.token, language, true)
    } else {
      // is login
      dispatch(toggleLoginModal({ show: false }))
      dispatch(setAuthenticationData({ type: 'weibo', result }))
    }
  })
}

// LOGOUT
function logout() {
  return async (dispatch) => {
    await dispatch(removeAuthenticationData())
    return dispatch({ type: authActions.LOGOUT })
  }
}

function handleLogout(state) {
  deleteCookie(COOKIE_KEYS.TOKEN)
  deleteCookie(COOKIE_KEYS.REMEMBER_ME_AUTHENTICATION)
  screenApi.removeHeader('Authorization')
  return chainReducers(clearHandler('currentUsers'), clearHandler('stripeAccounts'))(state)
}

const accountCustomHandlers = {
  [authActions.LOGOUT]: handleLogout,
}

export {
  // actions
  createRenewPassword,
  updateRenewPassword,
  validateAccount,
  resendAccountValidationEmail,
  setAuthenticationData,
  registerWithEmail,
  loginWithEmail,
  authByCookie,
  authBySocial,
  loginWithWeibo,
  logout,
  // handlers
  accountCustomHandlers,
}
